import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosApiInstance from '../../../utils/axiosConfig';
import { setLoading, setPersonInfo } from './physicianSlice';
import { ORG, ROOT, COGNITO_MAP } from '../../util/constants';
import { getImageUrlsFromS3 } from '../shared/sharedThunk';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '../../util/api';
import { loginPhysician } from '../auth/authThunk';
import { setSnackbarMessage, setSnackbarOpen } from '../shared/sharedSlice';

export const physicianApiSlice = createApi({
    reducerPath: 'physicianApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        getNPINumber: builder.query({
            query: ({ npi, firstName, lastName }) => ({
                url: `/nppes?org=${ORG}`,
                params: { npi, firstName, lastName },
            }),
        }),
        updateDocLatLong: builder.mutation({
            query: ({ loc }) => ({
                url: `/lat-long?org=${ORG}`,
                method: 'POST',
                body: { latLong: JSON.stringify(loc) },
            }),
        }),
        updateDocLocation: builder.mutation({
            query: ({ location, type }) => ({
                url: `/location?org=${ORG}`,
                method: 'POST',
                body: { location: JSON.stringify(location), type },
            }),
        }),
        updatePhysicianPreferredInsurance: builder.mutation({
            query: ({ insurances }) => ({
                url: `/insurance-preference?org=${ORG}`,
                method: 'PUT',
                body: { insurances },
            }),
        }),
        getPhysicianPreferredInsurance: builder.query({
            query: () => ({
            url: `/insurance-preference?org=${ORG}`,
            method: 'GET',
            }),
            transformResponse: (response) => response?.insurancePreference,
        }),
        getPhysicianLocation: builder.query({
            query: () => ({
            url: `/location?org=${ORG}`,
            method: 'GET',
            }),
            transformResponse: (response) => response?.locations,
        }),
        updatePhysicianPreferredSpecialty: builder.mutation({
            query: ({ specialties }) => ({
            url: `/specialty-preference?org=${ORG}`,
            method: 'PUT',
            body: { specialties },
            }),
        }),
        saveTraitStrengths: builder.mutation({
            query: ({ traitStrengthsDict }) => ({
                url: `/save-trait-strengths?org=${ORG}`,
                method: 'POST',
                body: { traitStrengthsDict },
            }),
        }),
        updatePersonInfo: builder.mutation({
            query: ({ newPersonInfo }) => ({
                url: `/person-info?org=${ORG}`,
                method: 'PUT',
                body: newPersonInfo,
            }),
        }),
        updateLastSeen: builder.mutation({
            query: ({ providerId }) => ({
                url: `/last-seen?providerId=${providerId}&org=${ORG}`,
                method: 'PUT',
            }),
        }),
        getGroups: builder.query({
            query: () => ({
                url: `/cognito-groups?org=${ORG}`,
                method: 'GET',
            }),
        }),
    }),
});

export const { 
    useLazyGetNPINumberQuery,
    useUpdateDocLatLongMutation,
    useUpdateDocLocationMutation,
    useUpdatePhysicianPreferredInsuranceMutation,
    useGetPhysicianPreferredInsuranceQuery,
    useGetPhysicianLocationQuery,
    useUpdatePhysicianPreferredSpecialtyMutation,
    useSaveTraitStrengthsMutation,
    useUpdatePersonInfoMutation,
    useUpdateLastSeenMutation,
    useGetGroupsQuery,
} = physicianApiSlice;

export const getLatLong = createAsyncThunk(
    'physician/getLatLong',
    async ({ location }) => {
    try {
        const response = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${location}&key=AIzaSyB0xLFvDaCgwUZXgAyR2alDcxjHfS68zIo`);
        const latLong = response?.data?.results[0]?.geometry?.location;
        return latLong;
    } catch(e) {
        console.log("Error getting lat long from google api");
        console.log("location: ")
        console.log(location)
        console.log(e);
    }
});

export const updateProfilePicture = createAsyncThunk(
    'physician/updateProfilePicture',
    async ({ image, personInfo }, { dispatch }) => {

    dispatch(setLoading(true));
    try {
        const formData = new FormData();
        formData.append('photo', image);

        const res = await axiosApiInstance.put(`${ROOT}/upload-avatar?org=${ORG}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if(res.status === 200) {
            const avatarUrl = res.data.url;
            const newPersonInfo = { ...personInfo, avatar: avatarUrl };
            dispatch(setPersonInfo(newPersonInfo));
            dispatch(setSnackbarMessage("Profile picture updated successfully."))
            dispatch(setSnackbarOpen(true));
        }
    } catch(e) {
        console.log("Error updating profile picture.");
        dispatch(setSnackbarMessage("Error updating profile picture. If this error persists, please contact Caralyst support."))
        dispatch(setSnackbarOpen(true));
        console.log(e)
    }
});

export const addUserToDocUserGroup = createAsyncThunk(
    'physician/addUserToDocUserGroup',
    async (_, { dispatch, getState }) => {
    try {
        const personInfo = getState().physician.personInfo;
        console.log(personInfo)
        const res = await axios.post(COGNITO_MAP["add_group"], { cognitoId: personInfo?.cognito_id });
        
        //Refresh auth token
        if(res?.status === 200) {
            dispatch(setSnackbarMessage("Success!"));
            await dispatch(loginPhysician());
        }
    } catch (e) {
        console.log(e);
    }
});